import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import videojs from 'video.js';
import { Observable, Subscription } from 'rxjs';
import { TextbookMobileDetectToken } from '../../../../../external/providers/mobile-detect-provider.service';
import * as MobileDetect from 'mobile-detect';

export enum SourceType {
  Video = 'video',
  Audio = 'audio',
}

export enum MediaPlayerPlaybackState {
  Loaded = 'loaded',
  Played = 'played',
  Paused = 'paused',
  Ended = 'ended',
}

export const MEDIA_PLAYER_VOLUME_KEY = 'mediaPlayerVolume';

export interface SourceMetaData {
  duration: number;
}

@Component({
  selector: 'txtb-media-player',
  templateUrl: './media-player.component.html',
  styleUrls: ['./media-player.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class MediaPlayerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() playerLayout: SourceType = SourceType.Video;
  @Input() playback$: Observable<boolean> | null | undefined;
  @Input() currentTime$: Observable<number> | null | undefined;
  @Input() controlsVisibility$: Observable<boolean> | null | undefined;
  @Input() volume$: Observable<number> | null | undefined;
  @Input() options: videojs.PlayerOptions = {};
  @Output() playerOnPlaybackChange = new EventEmitter<MediaPlayerPlaybackState>();
  @Output() playerOnReady = new EventEmitter<SourceMetaData>();
  @Output() playerOnAfterViewInit = new EventEmitter<void>();
  @Output() playerOnPlay = new EventEmitter<void>();
  @Output() playerOnPause = new EventEmitter<void>();
  @Output() playerOnEnded = new EventEmitter<boolean>();
  @Output() playerOnTimeUpdate = new EventEmitter<number>();
  @Output() playerOnVolumeChange = new EventEmitter<number>();
  @Output() playerOnLoadedData = new EventEmitter<SourceMetaData>();

  @ViewChild('video', { static: true }) $video!: ElementRef;

  player!: videojs.Player;
  private subscription = new Subscription();

  constructor(@Inject(TextbookMobileDetectToken) public mobileDetect: MobileDetect) {}

  ngOnInit(): void {
    this.player = videojs(this.$video.nativeElement, this.options, (): void => {
      this.playerOnPlaybackChange.emit(MediaPlayerPlaybackState.Loaded);
    });

    this.initLayout();
    this.initEvenets();

    this.subscription.add(
      this.playback$?.subscribe((playbackState) => (playbackState ? this.play() : this.pause()))
    );

    this.subscription.add(
      this.currentTime$?.subscribe((currentTime) => this.setCurrentTime(currentTime))
    );

    this.subscription.add(
      this.controlsVisibility$?.subscribe((visibility) => this.setControlsVisibility(visibility))
    );

    this.subscription.add(this.volume$?.subscribe((volume) => this.setVolume(volume)));
  }

  ngAfterViewInit(): void {
    this.playerOnAfterViewInit.emit();
  }

  initLayout(): void {
    const isMobile = this.mobileDetect.mobile() || this.mobileDetect.tablet();
    if (isMobile || this.isiOS()) {
      this.player.addClass('is-mobile');
    }

    if (this.player) {
      switch (this.playerLayout) {
        case SourceType.Video:
          this.player.addClass('video-layout');
          break;
        case SourceType.Audio:
          this.player.addClass('audio-layout');
          break;
      }
    }
  }

  initEvenets(): void {
    if (this.player) {
      this.player.on('touchstart', (e) => {
        if (e.target.nodeName === 'VIDEO') {
          this.player.paused() ? this.player.play() : this.player.pause();
        }
      });

      this.player.on('ended', () => {
        this.playerOnPlaybackChange.emit(MediaPlayerPlaybackState.Ended);
        this.playerOnEnded.emit(true);
      });

      this.player.on('play', () => {
        this.playerOnPlaybackChange.emit(MediaPlayerPlaybackState.Played);
        this.playerOnPlay.emit();
        this.playerOnEnded.emit(false);
      });

      this.player.on('pause', () => {
        this.playerOnPlaybackChange.emit(MediaPlayerPlaybackState.Paused);
        this.playerOnPause.emit();
      });

      this.player.on('volumechange', (e) => {
        const volume = this.player.muted() ? 0 : this.player.volume();
        this.playerOnVolumeChange.emit(volume);
      });

      this.player.on('timeupdate', () => this.playerOnTimeUpdate.emit(this.player.currentTime()));

      this.player.on('loadeddata', (e) => {
        const sourceMedaData: SourceMetaData = {
          duration: this.player.duration(),
        };

        this.playerOnLoadedData.emit(sourceMedaData);
      });
    }
  }

  contextMenu(event: any): void {
    event.preventDefault();
  }

  play(): void {
    if (this.player) {
      this.player.play();
    }
  }

  pause(): void {
    if (this.player) {
      this.player.pause();
    }
  }

  setCurrentTime(time: number): void {
    if (this.player) {
      this.player.currentTime(time);
    }
  }

  setControlsVisibility(visibility: boolean): void {
    if (this.player) {
      this.player.controls(visibility);
    }
  }

  setVolume(volume: number): void {
    if (this.player) {
      volume !== 0 ? this.player.volume(volume) : this.player.muted(true);
    }
  }

  isiOS(): boolean {
    return (
      ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
        navigator.platform
      ) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.player) {
      this.player.dispose();
    }
  }
}
